// The loan officer one-page application's API (web/go/src/intake/lo_onepager.go).
// Every call carries the LOS session token.

const authHeaders = (json = true): Record<string, string> => {
  const h: Record<string, string> = {
    Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"),
    Cache: "no-cache",
  };
  if (json) h["Content-Type"] = "application/json";
  return h;
};

export class LOOnePagerError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const parse = async (res: Response) => {
  let body: any = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok || !body || (body.status && body.status !== "OK")) {
    throw new LOOnePagerError(res.status, body?.error_message || body?.Message || `Request failed (${res.status})`);
  }
  return body;
};

export interface LOPerson {
  party_id: string;
  role: "borrower" | "coborrower";
  first_name: string;
  last_name: string;
  email: string;
}

export interface LODocument {
  serialid: number;
  filename: string;
  forwhom: "borrower" | "coborrower";
  doc_type: string;
  doc_title: string;
  parsed: boolean;
  failed: boolean;
  error?: string;
  fields: number;
  attached: boolean;
  uploaded?: string;
}

export interface LOJob {
  status: string;
  error_kind?: string;
  error?: string;
  attempts: number;
  first_attempted_at?: string;
  last_attempted_at?: string;
  next_retry_at?: string;
  completed_at?: string;
  encompass_loan_id?: string;
}

export type LOStage =
  | "form"
  | "processing"
  | "processing_failed"
  | "review"
  | "syncing"
  | "sync_waiting"
  | "sync_failed"
  | "synced";

export interface LOStatus {
  status: string;
  loan_id: string;
  stage: LOStage;
  created_at?: string;
  borrower: LOPerson | null;
  coborrower?: LOPerson | null;
  summary: {
    purpose: string;
    loan_amount: string;
    sales_price: string;
    appraisal: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  documents: LODocument[];
  prefill?: LOJob;
  sync?: LOJob;
  encompass_loan_id?: string;
  encompass_loan_number?: string;
  encompass_instance_id?: string;
  encompass_loan_folder?: string;
  attached_count: number;
  consumer_connect: { enabled: boolean; sent_to?: string; sent_at?: string };
}

export interface LOProvenanceField {
  role: "borrower" | "coborrower" | "";
  path: string;
  value: string;
  doc_serialid: number;
  doc_type: string;
  doc_title: string;
  filename: string;
}

export interface LOAccess {
  status: string;
  allowed: boolean;
  encompass_loid: string;
  los_url: string;
}

export const getLOOnePagerAccess = async (): Promise<LOAccess | null> => {
  const res = await fetch("/los/onepager/access", { headers: authHeaders(false) });
  if (res.status === 403) return null;
  return parse(res);
};

// ---------------------------------------------------------------------------
// Link session. The page opens from a link, not from a signed-in LOS session:
// it trades the link (customer + Encompass user id, plus the application's
// key once one exists) for a token scoped to this page, and keeps it where
// the rest of the LOS -- the embedded 1003 included -- expects a token.
// ---------------------------------------------------------------------------

let linkContext = { customerId: "", encompassLoid: "" };

export const configureLOLink = (ctx: { customerId: string; encompassLoid: string }) => {
  linkContext = ctx;
};

export const startLOLinkSession = async (loanId?: string, key?: string): Promise<void> => {
  const res = await fetch("/los/onepager/link-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      customer_id: linkContext.customerId,
      encompass_loid: linkContext.encompassLoid,
      loan_id: loanId || "",
      key: key || "",
    }),
  });
  const body = await parse(res);
  if (body?.token) sessionStorage.setItem("ZeitroA", body.token);
};

export const createLOApplication = async (payload: Record<string, any>): Promise<{ loan_id: string; key: string }> => {
  const res = await fetch("/los/onepager/applications", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const created = await parse(res);
  // The link token only creates; everything from the upload on needs the
  // token for this application.
  await startLOLinkSession(created.loan_id, created.key);
  return created;
};

export const uploadLODocuments = async (loanId: string, files: File[], coFiles: File[]): Promise<{ doc_ids: number[] }> => {
  const form = new FormData();
  files.forEach((f) => form.append("upload", f));
  coFiles.forEach((f) => form.append("upload_coborrower", f));
  const res = await fetch(`/los/onepager/applications/${loanId}/documents`, {
    method: "POST",
    headers: authHeaders(false),
    body: form,
  });
  return parse(res);
};

export const processLOApplication = async (loanId: string) => {
  const res = await fetch(`/los/onepager/applications/${loanId}/process`, { method: "POST", headers: authHeaders() });
  return parse(res);
};

export const getLOStatus = async (loanId: string): Promise<LOStatus> => {
  const res = await fetch(`/los/onepager/applications/${loanId}`, { headers: authHeaders(false) });
  return parse(res);
};

export const getLOProvenance = async (loanId: string): Promise<LOProvenanceField[]> => {
  const res = await fetch(`/los/onepager/applications/${loanId}/provenance`, { headers: authHeaders(false) });
  const body = await parse(res);
  return body.fields || [];
};

export const syncLOApplication = async (loanId: string) => {
  const res = await fetch(`/los/onepager/applications/${loanId}/sync`, { method: "POST", headers: authHeaders() });
  return parse(res);
};

export const retryLOApplication = async (loanId: string) => {
  const res = await fetch(`/los/onepager/applications/${loanId}/retry`, { method: "POST", headers: authHeaders() });
  return parse(res);
};

export const skipLODocuments = async (loanId: string) => {
  const res = await fetch(`/los/onepager/applications/${loanId}/skip-documents`, { method: "POST", headers: authHeaders() });
  return parse(res);
};

// The 1003 as the LOS reads it (the same call Interview1003ViewV2 makes).
export const getLOApplication1003 = async (loanId: string): Promise<any> => {
  const res = await fetch(`/los/v2/loans/${loanId}/application`, { headers: authHeaders(false) });
  const body = await res.json();
  if (!res.ok || body.Status !== "OK") throw new LOOnePagerError(res.status, body?.Message || "Could not load the 1003");
  return body.Application;
};

// One uploaded document's bytes, for the preview drawer.
export const downloadLODocument = async (
  loanId: string,
  docId: number
): Promise<{ content: string; mime: string; name: string } | null> => {
  const res = await fetch("/los/downloaddocuments", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ LoanID: loanId, DocID: docId }),
  });
  if (!res.ok) throw new LOOnePagerError(res.status, "Could not load the document");
  const body = await res.json();
  const doc = body?.Documents?.[0];
  if (!doc) return null;
  return { content: doc.Content, mime: doc.Mime, name: doc.Name };
};

// The signed-in loan officer, for the header.
export const getLOProfile = async (): Promise<{ firstName: string; lastName: string; email: string } | null> => {
  const res = await fetch("/los/getprofile", { headers: authHeaders(false) });
  if (!res.ok) return null;
  const body = await res.json();
  const lo = body?.LoanOfficers?.[0];
  if (!lo) return null;
  return { firstName: lo.FirstName || "", lastName: lo.LastName || "", email: lo.Email || "" };
};

// The company's logo and name, as the borrower one-pager shows them.
export const getCompanyBranding = async (customerId: string): Promise<{ logo: string; name: string }> => {
  const res = await fetch("/data/companycustomization", { headers: { "X-CustomerId": customerId } });
  if (!res.ok) return { logo: "", name: "" };
  const json = await res.json();
  let logo = "";
  try {
    logo = JSON.parse(json.Customization)?.icon || "";
  } catch (e) {
    logo = "";
  }
  return { logo, name: json.CompanyName || "" };
};
